import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { trpc } from '@/api/trpc';
import { useAppQuery } from '@/hooks/useAppQuery';
import { showToast } from '@/components/Layout';
import { Badge, Button, FormCheckbox, FormInput, FormSelect } from '@/components/UI';
import type { TradeLogicComparisonOperatorV2 } from '@/types/appRouter';
import {
    createLogicGroup,
    createLogicRootGroup,
    createLogicSchedule,
    createLogicSequence,
    createTradeLogicDraft,
    duplicateLogicNode,
    getLogicNode,
    getLogicNodeLocation,
    moveLogicNode,
    normalizeImportedRawConditionNodes,
    removeLogicNode,
    updateLogicNode,
    validateLogicTreeShape,
    wrapLogicNode,
    unwrapLogicNode,
    type LogicActionNode,
    type LogicConditionNode,
    type LogicGroupNode,
    type LogicNode,
    type LogicReferenceNode,
    type LogicScheduleNode,
    type LogicSequenceNode,
    type TradeLogicDiagnostic,
    type TradeLogicDraft,
    type TradeLogicMissingDataPolicy,
    type UpdateTradeLogicDraftMetadataInput
} from '../model';
import {
    mapFeatureRefValidationResultToDiagnostics,
    useTradeLogicAuthoringFeatures,
    useTradeLogicFeatureRefValidation,
    useTradeLogicRegistryManifest,
    useTradeLogicRegistryTemplates,
    validateTradeLogicDraftAgainstRegistry,
    type TradeLogicRegistryActionTemplateDto,
    type TradeLogicRegistryConditionTemplateDto,
    type TradeLogicRegistryFeatureDto
} from '../registry';
import {
    buildTradeLogicAuthoringOperationPayloads,
    buildTradeLogicAuthoringPreviewPayload,
    createLocalStorageTradeLogicAuthoringDraftPersistenceAdapter,
    hydrateTradeLogicDraftFromBackend,
    serializeContextFeatureRefForValidation,
    useLoadPublishedTradeLogicDraftMutation,
    useSaveIdeaLabVariantRulesMutation,
    usePublishTradeLogicAuthoringDraftInContextMutation
} from '../api';
import { applySeedConditionToDraft, parseTradeLogicSeedCondition } from '../seedCondition';
import type { TradeLogicEditorContext, TradeLogicEditorLoadSource } from '../tradeLogicNavigation';
import DraftTreePanel from './DraftTreePanel';
import FeatureTypeahead from './FeatureTypeahead';
import FeatureConditionValueInput from './FeatureConditionValueInput';
import NodeInspectorPanel from './NodeInspectorPanel';
import { collectSequencePriceCaptureTargets } from './sequencePriceCaptures';
import {
    createDraftBuilderInputStateByKind,
    updateDraftBuilderActionTemplateSelection,
    updateDraftBuilderConditionTemplateFeatureOptionSelection,
    updateDraftBuilderConditionTemplateSelection,
    updateDraftBuilderFeatureSelection,
    type DraftBuilderAddKind,
    type DraftBuilderInputState
} from './draftBuilderInputState';
import {
    buildActionTemplateNode,
    buildConditionTemplateNode,
    buildFeatureConditionNode,
    getFeatureConditionBlockingReason
} from './nodeCreationBuilders';
import { buildConditionTemplateValueField, filterAuthoringFeatures, groupRegistryFeatures } from './schemaControlUtils';
import SchemaFieldInput from './SchemaFieldInput';
import { appendNodeAtResolvedInsertionTarget } from './draftInsertionTarget';
import { filterTradeLogicActionTemplatesForKind, filterTradeLogicConditionTemplatesForKind } from './templateKindFilter';

const createNodeId = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const createInitialDraft = (kind: 'entry' | 'exit'): TradeLogicDraft =>
    createTradeLogicDraft({
        draftId: `local-${kind}-new-${Date.now()}`,
        name: `New ${kind} rules`,
        kind,
        scope: kind === 'entry' ? 'asset' : 'trade',
        schemaVersion: 1,
        rootNode: createLogicRootGroup({ createId: createNodeId }),
        evaluationModes: ['backtest', 'live']
    });

const RULE_EDITOR_DRAFT_STORAGE_VERSION = 2;
const ruleEditorDraftPersistence = createLocalStorageTradeLogicAuthoringDraftPersistenceAdapter();

interface RuleEditorDraftSnapshot {
    version: typeof RULE_EDITOR_DRAFT_STORAGE_VERSION;
    savedAt: string;
    kind: 'entry' | 'exit';
    definitionId: number | null;
    draft: TradeLogicDraft;
}

const buildRuleEditorDraftStorageScopeKey = ({
    definitionId,
    ideaLabVariantId,
    kind,
    saveContext,
    sourceDefinitionId,
    sourceMode,
    sourceVersion
}: {
    definitionId: number | null;
    ideaLabVariantId: number | null;
    kind: 'entry' | 'exit';
    saveContext?: TradeLogicEditorContext;
    sourceDefinitionId: number | null;
    sourceMode: TradeLogicEditorLoadSource['mode'] | null;
    sourceVersion: number | null;
}) => {
    const strategyScope = saveContext?.mode === 'strategy' ? `strategy:${saveContext.strategyId ?? 'new'}` : 'library';

    if (ideaLabVariantId !== null) {
        return `rule-editor:${kind}:ideaLab:variant:${ideaLabVariantId}`;
    }

    if (definitionId !== null) {
        return `rule-editor:${kind}:definition:${definitionId}:version:${sourceVersion ?? 'latest'}:${strategyScope}`;
    }

    if (sourceMode === 'copyFromPublished' && sourceDefinitionId !== null) {
        return `rule-editor:${kind}:copy:${sourceDefinitionId}:version:${sourceVersion ?? 'latest'}:${strategyScope}`;
    }

    return `rule-editor:${kind}:new:${strategyScope}`;
};

const buildRuleEditorDraftStorageKey = ({
    definitionId,
    ideaLabVariantId,
    kind,
    saveContext,
    sourceDefinitionId,
    sourceMode,
    sourceVersion
}: {
    definitionId: number | null;
    ideaLabVariantId: number | null;
    kind: 'entry' | 'exit';
    saveContext?: TradeLogicEditorContext;
    sourceDefinitionId: number | null;
    sourceMode: TradeLogicEditorLoadSource['mode'] | null;
    sourceVersion: number | null;
}) => ({
    scopeKey: buildRuleEditorDraftStorageScopeKey({
        definitionId,
        ideaLabVariantId,
        kind,
        saveContext,
        sourceDefinitionId,
        sourceMode,
        sourceVersion
    }),
    strategyId: encodeRuleEditorStorageStrategyId(kind, definitionId)
});

const encodeRuleEditorStorageStrategyId = (kind: 'entry' | 'exit', definitionId: number | null) => {
    const kindOffset = kind === 'entry' ? 1_000_000_000 : 2_000_000_000;
    const normalizedDefinitionId = definitionId ?? 0;
    return kindOffset + normalizedDefinitionId;
};

const loadRuleEditorDraftSnapshot = ({
    definitionId,
    ideaLabVariantId,
    kind,
    saveContext,
    sourceDefinitionId,
    sourceMode,
    sourceVersion
}: {
    definitionId: number | null;
    ideaLabVariantId: number | null;
    kind: 'entry' | 'exit';
    saveContext?: TradeLogicEditorContext;
    sourceDefinitionId: number | null;
    sourceMode: TradeLogicEditorLoadSource['mode'] | null;
    sourceVersion: number | null;
}): RuleEditorDraftSnapshot | null => {
    const snapshot = ruleEditorDraftPersistence.loadWorkspaceDraft(
        buildRuleEditorDraftStorageKey({
            definitionId,
            ideaLabVariantId,
            kind,
            saveContext,
            sourceDefinitionId,
            sourceMode,
            sourceVersion
        })
    );

    if (snapshot?.version !== RULE_EDITOR_DRAFT_STORAGE_VERSION) return null;
    if (snapshot.entryDraft.kind !== kind) return null;

    return {
        version: RULE_EDITOR_DRAFT_STORAGE_VERSION,
        savedAt: snapshot.savedAt,
        kind,
        definitionId,
        draft: {
            ...snapshot.entryDraft,
            rootNode: normalizeImportedRawConditionNodes(snapshot.entryDraft.rootNode)
        }
    };
};

const saveRuleEditorDraftSnapshot = ({
    definitionId,
    draft,
    ideaLabVariantId,
    kind,
    saveContext,
    sourceDefinitionId,
    sourceMode,
    sourceVersion
}: {
    definitionId: number | null;
    draft: TradeLogicDraft;
    ideaLabVariantId: number | null;
    kind: 'entry' | 'exit';
    saveContext?: TradeLogicEditorContext;
    sourceDefinitionId: number | null;
    sourceMode: TradeLogicEditorLoadSource['mode'] | null;
    sourceVersion: number | null;
}) => {
    const normalizedDraft = {
        ...draft,
        rootNode: normalizeImportedRawConditionNodes(draft.rootNode)
    };

    ruleEditorDraftPersistence.saveWorkspaceDraft(
        buildRuleEditorDraftStorageKey({
            definitionId,
            ideaLabVariantId,
            kind,
            saveContext,
            sourceDefinitionId,
            sourceMode,
            sourceVersion
        }),
        {
            version: RULE_EDITOR_DRAFT_STORAGE_VERSION,
            savedAt: new Date().toISOString(),
            strategyId: encodeRuleEditorStorageStrategyId(kind, definitionId),
            registryManifestFingerprint: normalizedDraft.registryManifestFingerprint ?? null,
            entryDraft: normalizedDraft,
            exitDraft: normalizedDraft,
            lastLocalValidationAt: null
        }
    );
};

const clearRuleEditorDraftSnapshot = ({
    definitionId,
    ideaLabVariantId,
    kind,
    saveContext,
    sourceDefinitionId,
    sourceMode,
    sourceVersion
}: {
    definitionId: number | null;
    ideaLabVariantId: number | null;
    kind: 'entry' | 'exit';
    saveContext?: TradeLogicEditorContext;
    sourceDefinitionId: number | null;
    sourceMode: TradeLogicEditorLoadSource['mode'] | null;
    sourceVersion: number | null;
}) => {
    ruleEditorDraftPersistence.clearWorkspaceDraft(
        buildRuleEditorDraftStorageKey({
            definitionId,
            ideaLabVariantId,
            kind,
            saveContext,
            sourceDefinitionId,
            sourceMode,
            sourceVersion
        })
    );
};

const missingDataPolicyOptions: { value: '' | TradeLogicMissingDataPolicy; label: string }[] = [
    { value: '', label: 'Default' },
    { value: 'fail', label: 'Fail' },
    { value: 'treatAsFalse', label: 'Treat as false' },
    { value: 'treatAsUnknown', label: 'Treat as unknown' },
    { value: 'carryForward', label: 'Carry forward' },
    { value: 'skipNode', label: 'Skip node' }
];

const ADD_RULE_TABS: { value: DraftBuilderAddKind; label: string }[] = [
    { value: 'condition', label: 'Condition' },
    { value: 'action', label: 'Action' },
    { value: 'group', label: 'Group' }
];

interface TradeLogicRuleEditorProps {
    kind: 'entry' | 'exit';
    definitionId: number | null;
    initialVersion?: number | null;
    onSaved?: (result: { definitionId: number; version: number; versionId: number }, meta?: { appliedToStrategy: boolean }) => void;
    saveContext?: TradeLogicEditorContext;
    sourceDefinitionId?: number | null;
    sourceMode?: TradeLogicEditorLoadSource['mode'] | null;
    /**
     * Raw `seedCondition` URL param. Consumed only on a fresh `copyFromPublished` load: the parsed veto
     * conditions are pre-added to the copied draft (regime-profile "Create gated variant" flow). A draft
     * restored from local storage already contains its seed and is never re-seeded.
     */
    seedConditionRaw?: string | null;
    /**
     * Idea Lab mode: the editor opens on a draft variant's stored rules and Save writes them back to
     * that variant instead of publishing a Trade Logic version.
     */
    ideaLabVariant?: IdeaLabEditorTarget | null;
    onSavedToIdeaLab?: (variantId: number) => void;
}

export interface IdeaLabEditorTarget {
    variantId: number;
    studyId: number;
    label: string;
    payload: Parameters<typeof hydrateTradeLogicDraftFromBackend>[0];
}

const TradeLogicRuleEditor = ({
    kind,
    definitionId,
    initialVersion = null,
    onSaved,
    saveContext,
    sourceDefinitionId = null,
    sourceMode = null,
    seedConditionRaw = null,
    ideaLabVariant = null,
    onSavedToIdeaLab
}: TradeLogicRuleEditorProps) => {
    const ideaLabVariantId = ideaLabVariant?.variantId ?? null;
    // The load effect keys on the variant id; the payload is read through a ref so a refetched or
    // re-created target object never resets rules being edited.
    const ideaLabPayloadRef = useRef(ideaLabVariant?.payload ?? null);

    ideaLabPayloadRef.current = ideaLabVariant?.payload ?? null;
    const publishedSourceDefinitionId = sourceDefinitionId ?? definitionId;
    const publishedSourceVersion = initialVersion;
    const [draft, setDraft] = useState<TradeLogicDraft>(() => createInitialDraft(kind));
    const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
    const [builderInput, setBuilderInput] = useState<DraftBuilderInputState>(() => createDraftBuilderInputStateByKind()[kind]);
    const [backendDiagnostics, setBackendDiagnostics] = useState<TradeLogicDiagnostic[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [lastSavedSummary, setLastSavedSummary] = useState<string | null>(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [isLoadingDraft, setIsLoadingDraft] = useState(publishedSourceDefinitionId !== null);
    const [restoredDraftSavedAt, setRestoredDraftSavedAt] = useState<string | null>(null);

    const canLoadSwingExit =
        kind === 'exit' &&
        publishedSourceDefinitionId === null &&
        ideaLabVariantId === null &&
        (!draft.rootNode || (draft.rootNode.kind === 'group' && draft.rootNode.children.length === 0));
    const swingExitQuery = useAppQuery(
        {
            ...trpc.tradeLogicAuthoring.getSwingTemplate.queryOptions({ template: 'stagedExit' }),
            enabled: canLoadSwingExit,
            staleTime: 60_000
        },
        { fallbackErrorMessage: 'Failed to load swing exit template' }
    );

    const manifestQuery = useTradeLogicRegistryManifest({ enabled: true });

    const featuresQuery = useTradeLogicAuthoringFeatures({
        enabled: true,
        surfaces: ['rustBacktest', 'rustLive'],
        definitionKind: kind,
        discoverable: undefined,
        requireAuthorable: false
    });

    const templatesQuery = useTradeLogicRegistryTemplates({ enabled: true });

    const loadPublishedDraftMutation = useLoadPublishedTradeLogicDraftMutation();
    const saveIdeaLabVariantRulesMutation = useSaveIdeaLabVariantRulesMutation();

    const publishDraftMutation = usePublishTradeLogicAuthoringDraftInContextMutation({
        kind,
        saveContext
    });

    useEffect(() => {
        let cancelled = false;

        setSelectedNodeId(null);
        setBuilderInput(createDraftBuilderInputStateByKind()[kind]);
        setBackendDiagnostics([]);
        setLastSavedSummary(null);
        setLoadError(null);
        setRestoredDraftSavedAt(null);
        setHasUnsavedChanges(false);

        const restoredSnapshot = loadRuleEditorDraftSnapshot({
            definitionId,
            ideaLabVariantId,
            kind,
            saveContext,
            sourceDefinitionId: publishedSourceDefinitionId,
            sourceMode,
            sourceVersion: publishedSourceVersion
        });

        if (restoredSnapshot) {
            setDraft(restoredSnapshot.draft);
            setRestoredDraftSavedAt(restoredSnapshot.savedAt);
            setHasUnsavedChanges(true);
            setIsLoadingDraft(false);

            return () => {
                cancelled = true;
            };
        }

        if (ideaLabVariantId !== null && ideaLabPayloadRef.current !== null) {
            setDraft(hydrateTradeLogicDraftFromBackend(ideaLabPayloadRef.current));
            setIsLoadingDraft(false);

            return () => {
                cancelled = true;
            };
        }

        if (publishedSourceDefinitionId === null) {
            setDraft(createInitialDraft(kind));
            setIsLoadingDraft(false);

            return () => {
                cancelled = true;
            };
        }

        setIsLoadingDraft(true);

        loadPublishedDraftMutation
            .mutateAsync({
                definitionId: publishedSourceDefinitionId,
                ...(publishedSourceVersion !== null ? { version: publishedSourceVersion } : {}),
                ...(sourceMode === 'copyFromPublished' ? { mode: 'fork' as const } : { mode: 'revise' as const }),
                ...(saveContext?.mode === 'strategy' ? { strategyId: saveContext.strategyId } : {})
            })
            .then((result) => {
                if (cancelled) return;

                if (result.draft) {
                    const hydratedDraft = hydrateTradeLogicDraftFromBackend(result.draft);
                    const seedParse =
                        sourceMode === 'copyFromPublished' ? parseTradeLogicSeedCondition(seedConditionRaw) : { status: 'absent' as const };
                    const seedResult = seedParse.status === 'ok' ? applySeedConditionToDraft(hydratedDraft, seedParse.seed) : null;
                    const seedApplied = seedResult !== null && seedResult.applied;

                    setDraft(seedApplied ? seedResult.draft : hydratedDraft);
                    setSelectedNodeId(null);
                    // A seeded draft already differs from the published source, so it counts as an
                    // unsaved change (and persists like one).
                    setHasUnsavedChanges(seedApplied);
                    setIsLoadingDraft(false);

                    if (seedResult !== null) {
                        if (seedResult.applied) {
                            const skippedNote =
                                seedResult.skippedExistingStates.length > 0
                                    ? ` Already vetoed: ${seedResult.skippedExistingStates.join(', ')}.`
                                    : '';

                            showToast(
                                `Added regime veto condition${seedResult.seededStates.length === 1 ? '' : 's'} for ${seedResult.seededStates.join(', ')} to the copied rules.${skippedNote}`,
                                'info'
                            );
                        } else {
                            showToast(
                                `The copied entry already vetoes ${seedResult.skippedExistingStates.join(', ')} — nothing was added.`,
                                'info'
                            );
                        }
                    }

                    return;
                }

                setLoadError('Saved rules are unavailable for editing.');
                setIsLoadingDraft(false);
            })
            .catch((error) => {
                if (cancelled) return;

                setLoadError(error instanceof Error ? error.message : 'Failed to load saved rules.');
                setIsLoadingDraft(false);
            });

        return () => {
            cancelled = true;
        };
    }, [
        definitionId,
        ideaLabVariantId,
        kind,
        publishedSourceDefinitionId,
        publishedSourceVersion,
        saveContext?.mode,
        saveContext?.strategyId,
        seedConditionRaw,
        sourceMode
    ]);

    useEffect(() => {
        if (isLoadingDraft) return;

        if (!hasUnsavedChanges) {
            clearRuleEditorDraftSnapshot({
                definitionId,
                ideaLabVariantId,
                kind,
                saveContext,
                sourceDefinitionId: publishedSourceDefinitionId,
                sourceMode,
                sourceVersion: publishedSourceVersion
            });

            return;
        }

        saveRuleEditorDraftSnapshot({
            definitionId,
            draft,
            ideaLabVariantId,
            kind,
            saveContext,
            sourceDefinitionId: publishedSourceDefinitionId,
            sourceMode,
            sourceVersion: publishedSourceVersion
        });
    }, [
        definitionId,
        draft,
        hasUnsavedChanges,
        ideaLabVariantId,
        isLoadingDraft,
        kind,
        publishedSourceDefinitionId,
        publishedSourceVersion,
        saveContext?.mode,
        saveContext?.strategyId,
        sourceMode
    ]);

    const selectedNode = useMemo(() => getLogicNode(draft.rootNode, selectedNodeId ?? ''), [draft.rootNode, selectedNodeId]);
    const priceCaptureOptions = useMemo(() => (draft.rootNode ? collectSequencePriceCaptureTargets(draft.rootNode) : []), [draft.rootNode]);

    const localTreeDiagnostics = useMemo(() => validateLogicTreeShape(draft.rootNode), [draft.rootNode]);

    const registryReady =
        manifestQuery.isSuccess &&
        featuresQuery.isSuccess &&
        templatesQuery.actionTemplatesQuery.isSuccess &&
        templatesQuery.conditionTemplatesQuery.isSuccess;

    const registryDiagnostics = useMemo(() => {
        if (!registryReady) return [];
        return validateTradeLogicDraftAgainstRegistry({
            actionTemplatesByKey: templatesQuery.actionTemplatesByKey,
            conditionTemplatesByKey: templatesQuery.conditionTemplatesByKey,
            draft,
            featuresByKey: featuresQuery.featuresByKey,
            manifest: manifestQuery.data,
            registryManifestFingerprint: manifestQuery.data?.semanticFingerprint
        });
    }, [
        draft,
        featuresQuery.featuresByKey,
        manifestQuery.data,
        registryReady,
        templatesQuery.actionTemplatesByKey,
        templatesQuery.conditionTemplatesByKey
    ]);

    const featureRefValidationInput = useMemo(
        () => (selectedNode?.kind === 'condition' ? serializeContextFeatureRefForValidation(selectedNode.featureRef) : null),
        [selectedNode]
    );

    const featureRefValidationQuery = useTradeLogicFeatureRefValidation({
        enabled: true,
        featureRef: featureRefValidationInput
    });

    const featureRefDiagnostics = useMemo(
        () =>
            mapFeatureRefValidationResultToDiagnostics(
                selectedNode?.kind === 'condition' ? selectedNode : null,
                featureRefValidationQuery.data
            ),
        [featureRefValidationQuery.data, selectedNode]
    );

    const featureRefValidationState: 'idle' | 'checking' | 'valid' | 'invalid' | 'error' = featureRefValidationQuery.isLoading
        ? 'checking'
        : featureRefValidationQuery.isError
          ? 'error'
          : featureRefValidationQuery.data
            ? featureRefValidationQuery.data.isValid
                ? 'valid'
                : 'invalid'
            : 'idle';

    const allDiagnostics = useMemo(
        () => [...localTreeDiagnostics, ...registryDiagnostics, ...featureRefDiagnostics, ...backendDiagnostics],
        [localTreeDiagnostics, registryDiagnostics, featureRefDiagnostics, backendDiagnostics]
    );

    const errorCount = allDiagnostics.filter((d) => d.severity === 'error').length;

    const saveButtonLabel = ideaLabVariant
        ? 'Save to Idea Lab variant'
        : saveContext?.mode === 'strategy'
          ? saveContext.strategyId !== null
              ? 'Publish + Apply to Strategy'
              : 'Publish + Return to Strategy'
          : 'Publish Version';

    const saveContextMessage = ideaLabVariant
        ? `Saving here updates draft variant "${ideaLabVariant.label}" (#${ideaLabVariant.variantId}) in Idea Lab study #${ideaLabVariant.studyId}. Nothing is published; freeze the variant in Idea Lab to evaluate it.`
        : saveContext?.mode === 'strategy'
          ? saveContext.strategyId !== null
              ? sourceMode === 'copyFromPublished'
                  ? `Saving here publishes the first revision of a strategy-owned fork and applies it to Strategy ${saveContext.strategyId} immediately.`
                  : `Saving here publishes a new revision on this rule set and applies it to Strategy ${saveContext.strategyId} immediately.`
              : sourceMode === 'copyFromPublished'
                ? `Saving here publishes the first revision of a strategy-owned fork and returns it to the strategy draft. It will attach when the strategy is created.`
                : `Saving here publishes a new revision on this rule set and returns it to the strategy draft. It will attach when the strategy is created.`
          : null;

    const sourceDefinitionMessage =
        publishedSourceDefinitionId !== null
            ? sourceMode === 'copyFromPublished'
                ? `Started from saved definition #${publishedSourceDefinitionId}${publishedSourceVersion !== null ? ` v${publishedSourceVersion}` : ''}. Publishing creates a new forked rule set; the source stays unchanged.`
                : `Editing rule set #${publishedSourceDefinitionId}${publishedSourceVersion !== null ? ` from saved v${publishedSourceVersion}` : ''}. Publishing creates a new revision on this rule set.`
            : null;

    const computedState = useMemo(() => {
        const totalFeatureCount = filterAuthoringFeatures(featuresQuery.data, true).length;
        const visibleFeatures = filterAuthoringFeatures(featuresQuery.data, builderInput.showAdvancedFeatures);
        const grouped = groupRegistryFeatures(visibleFeatures, builderInput.featureSearch);
        const filteredFeatureCount = grouped.reduce((accumulator, group) => accumulator + group.features.length, 0);

        const passesTier = (tier: 'primary' | 'secondary' | 'advanced' | null) =>
            builderInput.showAdvancedFeatures || tier === 'primary' || tier === null;

        const filteredActionTemplates = filterTradeLogicActionTemplatesForKind(templatesQuery.actionTemplates, kind).filter((template) =>
            passesTier(template.tier)
        );

        const filteredConditionTemplates = filterTradeLogicConditionTemplatesForKind(templatesQuery.conditionTemplates, kind).filter(
            (template) => passesTier(template.tier)
        );

        const selectedFeature = builderInput.selectedFeatureKey
            ? (featuresQuery.featuresByKey[builderInput.selectedFeatureKey] ?? null)
            : null;

        const selectedActionTemplate = builderInput.selectedActionTemplateKey
            ? (templatesQuery.actionTemplatesByKey[builderInput.selectedActionTemplateKey] ?? null)
            : null;

        const selectedConditionTemplate = builderInput.selectedConditionTemplateKey
            ? (templatesQuery.conditionTemplatesByKey[builderInput.selectedConditionTemplateKey] ?? null)
            : null;

        const selectedConditionTemplateFeatureOption = selectedConditionTemplate
            ? (selectedConditionTemplate.contextFeatureOptions.find(
                  (option) => option.optionKey === builderInput.selectedConditionTemplateFeatureOptionKey
              ) ??
              selectedConditionTemplate.contextFeatureOptions[0] ??
              null)
            : null;

        return {
            featureOptions: [
                { label: '—', options: [{ value: '', label: 'Type to search' }] },
                ...grouped.map((group) => ({
                    label: group.label,
                    options: group.features.map((feature) => ({ value: feature.featureKey, label: feature.displayLabel }))
                }))
            ],
            actionTemplateOptions: [
                { value: '', label: 'Select action' },
                ...filteredActionTemplates.map((template) => ({ value: template.templateKey, label: template.displayLabel }))
            ],
            conditionTemplateOptions: [
                { value: '', label: 'Select template' },
                ...filteredConditionTemplates.map((template) => ({ value: template.templateKey, label: template.displayLabel }))
            ],
            conditionTemplateValueField: buildConditionTemplateValueField(selectedConditionTemplate),
            filteredFeatureCount,
            totalFeatureCount,
            selectedFeature,
            selectedActionTemplate,
            selectedConditionTemplate,
            selectedConditionTemplateFeatureOption,
            selectedFeatureConditionBlockingReason: getFeatureConditionBlockingReason(selectedFeature)
        };
    }, [builderInput, featuresQuery.data, featuresQuery.featuresByKey, kind, templatesQuery]);

    const updateBuilder = useCallback((changes: Partial<DraftBuilderInputState>) => {
        setBuilderInput((current) => ({ ...current, ...changes }));
    }, []);

    const markDirty = useCallback(() => {
        setBackendDiagnostics([]);
        setHasUnsavedChanges(true);
    }, []);

    const setRootNodeFromChange = useCallback(
        (rootNode: LogicNode | null, changed: boolean) => {
            if (!changed) return;
            setDraft((current) => ({ ...current, rootNode, updatedAt: new Date().toISOString() }));
            markDirty();
        },
        [markDirty]
    );

    const updateDraftMetadata = useCallback(
        (changes: UpdateTradeLogicDraftMetadataInput) => {
            setDraft((current) => ({
                ...current,
                ...(changes.name !== undefined ? { name: changes.name } : {}),
                ...(changes.description !== undefined ? { description: changes.description } : {}),
                updatedAt: new Date().toISOString()
            }));

            markDirty();
        },
        [markDirty]
    );

    const appendNodeToRoot = useCallback(
        (node: LogicNode) => {
            setDraft((current) => {
                const rootNode = current.rootNode ?? createLogicRootGroup({ createId: createNodeId });
                const change = appendNodeAtResolvedInsertionTarget(rootNode, node, selectedNodeId);
                return { ...current, rootNode: change.rootNode, updatedAt: new Date().toISOString() };
            });

            setSelectedNodeId(node.id);
            markDirty();
        },
        [markDirty, selectedNodeId]
    );

    const handleSelectNode = (nodeId: string) => setSelectedNodeId(nodeId);

    const handleToggleNode = (nodeId: string) => {
        const change = updateLogicNode(draft.rootNode, nodeId, (node) => ({ ...node, enabled: !node.enabled }));
        setRootNodeFromChange(change.rootNode, change.changed);
    };

    const handleRemoveNode = (nodeId: string) => {
        const change = removeLogicNode(draft.rootNode, nodeId);
        setRootNodeFromChange(change.rootNode, change.changed);
        if (selectedNodeId === nodeId) setSelectedNodeId(null);
    };

    const handleMoveNode = (nodeId: string, direction: 'up' | 'down') => {
        const location = getLogicNodeLocation(draft.rootNode, nodeId);
        const currentIndex = location?.path[location.path.length - 1] ?? -1;
        const nextIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        if (!location?.parent || nextIndex < 0 || nextIndex >= location.parent.children.length) return;

        const change = moveLogicNode(draft.rootNode, nodeId, { targetParentId: location.parent.id, index: nextIndex });
        setRootNodeFromChange(change.rootNode, change.changed);
    };

    const handleDuplicateNode = (nodeId: string) => {
        const change = duplicateLogicNode(draft.rootNode, nodeId, {
            idFactory: (sourceNode) => createNodeId(`${sourceNode.kind}-copy`)
        });

        setRootNodeFromChange(change.rootNode, change.changed);
        if (change.meta?.duplicatedNode.id) setSelectedNodeId(change.meta.duplicatedNode.id);
    };

    const handleWrapNode = (nodeId: string, wrapperKind: 'group' | 'sequence') => {
        const wrapper =
            wrapperKind === 'group'
                ? createLogicGroup({ label: 'Group' }, { createId: createNodeId })
                : createLogicSequence({ label: 'Sequence' }, { createId: createNodeId });

        const change = wrapLogicNode(draft.rootNode, nodeId, { wrapper });
        setRootNodeFromChange(change.rootNode, change.changed);
        if (change.meta?.wrapperNode.id) setSelectedNodeId(change.meta.wrapperNode.id);
    };

    const handleUnwrapNode = (nodeId: string) => {
        const change = unwrapLogicNode(draft.rootNode, nodeId);
        setRootNodeFromChange(change.rootNode, change.changed);
        if (change.meta?.promotedNodes[0]?.id) setSelectedNodeId(change.meta.promotedNodes[0].id);
    };

    const updateNode = useCallback(
        <T extends LogicNode>(nodeId: string, changes: Partial<T>) => {
            const change = updateLogicNode(draft.rootNode, nodeId, (node) => ({ ...node, ...changes }));
            setRootNodeFromChange(change.rootNode, change.changed);
        },
        [draft.rootNode, setRootNodeFromChange]
    );

    const handleLabelChange = (nodeId: string, label: string) => updateNode(nodeId, { label });
    const handleConditionChange = (nodeId: string, changes: Partial<LogicConditionNode>) => updateNode(nodeId, changes);
    const handleActionChange = (nodeId: string, changes: Partial<LogicActionNode>) => updateNode(nodeId, changes);
    const handleGroupChange = (nodeId: string, changes: Partial<LogicGroupNode>) => updateNode(nodeId, changes);
    const handleSequenceChange = (nodeId: string, changes: Partial<LogicSequenceNode>) => updateNode(nodeId, changes);
    const handleScheduleChange = (nodeId: string, changes: Partial<LogicScheduleNode>) => updateNode(nodeId, changes);
    const handleReferenceChange = (nodeId: string, changes: Partial<LogicReferenceNode>) => updateNode(nodeId, changes);

    const addStructureNode = (nodeKind: 'group' | 'sequence' | 'schedule') => {
        const node =
            nodeKind === 'group'
                ? createLogicGroup({ label: 'Group' }, { createId: createNodeId })
                : nodeKind === 'sequence'
                  ? createLogicSequence({ label: 'Sequence' }, { createId: createNodeId })
                  : createLogicSchedule({ label: 'Schedule' }, { createId: createNodeId });

        appendNodeToRoot(node);
    };

    const addFeatureCondition = () => {
        try {
            const node = buildFeatureConditionNode({
                createId: createNodeId,
                feature: computedState.selectedFeature,
                filterOperators: builderInput.filterOperators,
                filterValues: builderInput.filterValues,
                missingDataPolicy: builderInput.selectedMissingDataPolicy,
                operator: builderInput.selectedFeatureOperator,
                selectorValues: builderInput.selectorValues,
                value: builderInput.selectedFeatureValue
            });

            appendNodeToRoot(node);
            updateBuilder({ localError: null });
        } catch (error) {
            updateBuilder({ localError: error instanceof Error ? error.message : 'Failed to add condition' });
        }
    };

    const addConditionTemplate = () => {
        try {
            const node = buildConditionTemplateNode({
                createId: createNodeId,
                featureOption: computedState.selectedConditionTemplateFeatureOption,
                operator: builderInput.selectedConditionTemplateOperator,
                parameterValues: builderInput.conditionTemplateValues,
                template: computedState.selectedConditionTemplate,
                value: builderInput.selectedConditionTemplateValue
            });

            appendNodeToRoot(node);
            updateBuilder({ localError: null });
        } catch (error) {
            updateBuilder({ localError: error instanceof Error ? error.message : 'Failed to add condition template' });
        }
    };

    const addActionTemplate = () => {
        try {
            const node = buildActionTemplateNode({
                createId: createNodeId,
                documentKind: kind,
                parameterValues: builderInput.actionTemplateValues,
                template: computedState.selectedActionTemplate
            });

            appendNodeToRoot(node);
            updateBuilder({ localError: null });
        } catch (error) {
            updateBuilder({ localError: error instanceof Error ? error.message : 'Failed to add action' });
        }
    };

    const handleSelectFeatureKey = (featureKey: string) => {
        setBuilderInput((current) =>
            updateDraftBuilderFeatureSelection({
                featureKey,
                featuresByKey: featuresQuery.featuresByKey,
                state: current
            })
        );
    };

    const handleSelectActionTemplateKey = (templateKey: string) => {
        setBuilderInput((current) =>
            updateDraftBuilderActionTemplateSelection({
                actionTemplatesByKey: templatesQuery.actionTemplatesByKey,
                state: current,
                templateKey
            })
        );
    };

    const handleSelectConditionTemplateKey = (templateKey: string) => {
        setBuilderInput((current) =>
            updateDraftBuilderConditionTemplateSelection({
                conditionTemplatesByKey: templatesQuery.conditionTemplatesByKey,
                state: current,
                templateKey
            })
        );
    };

    const handleSelectConditionTemplateFeatureOptionKey = (optionKey: string) => {
        setBuilderInput((current) =>
            updateDraftBuilderConditionTemplateFeatureOptionSelection({
                optionKey,
                state: current,
                template: computedState.selectedConditionTemplate
            })
        );
    };

    const operationPayloads = useMemo(() => {
        const previewPayload = buildTradeLogicAuthoringPreviewPayload({
            diagnostics: allDiagnostics,
            draft,
            registryManifestFingerprint: manifestQuery.data?.semanticFingerprint,
            strategyId: saveContext?.mode === 'strategy' ? saveContext.strategyId : null
        });

        return buildTradeLogicAuthoringOperationPayloads(previewPayload);
    }, [allDiagnostics, draft, manifestQuery.data?.semanticFingerprint, saveContext]);

    const handleSave = async () => {
        if (errorCount > 0) {
            showToast(`Resolve ${errorCount} error${errorCount === 1 ? '' : 's'} before saving.`, 'warning');
            return;
        }

        setIsSaving(true);
        setLastSavedSummary(null);

        if (ideaLabVariant !== null) {
            try {
                await saveIdeaLabVariantRulesMutation.mutateAsync({
                    variantId: ideaLabVariant.variantId,
                    label: draft.name,
                    description: draft.description || null,
                    authoringPayload: operationPayloads.saveDraft.draft
                });
                setBackendDiagnostics([]);
                setHasUnsavedChanges(false);
                clearRuleEditorDraftSnapshot({
                    definitionId,
                    ideaLabVariantId,
                    kind,
                    saveContext,
                    sourceDefinitionId: publishedSourceDefinitionId,
                    sourceMode,
                    sourceVersion: publishedSourceVersion
                });
                setLastSavedSummary(`Saved to Idea Lab variant #${ideaLabVariant.variantId}`);
                showToast(`Saved the rules to Idea Lab variant #${ideaLabVariant.variantId}.`, 'success');
                onSavedToIdeaLab?.(ideaLabVariant.variantId);
            } catch (error) {
                showToast(error instanceof Error ? error.message : 'Save failed.', 'error');
            } finally {
                setIsSaving(false);
            }

            return;
        }

        try {
            const { publishResult: publish, appliedToStrategy } = await publishDraftMutation.mutateAsync({
                ...operationPayloads.publishDraft,
                publishMode: sourceMode === 'copyFromPublished' ? 'fork' : 'revise'
            });

            if (!publish.published || publish.definitionId === null || publish.versionId === null) {
                const publishDiagnostics = publish.compilePreview.canCompile
                    ? publish.validation.diagnostics
                    : publish.compilePreview.diagnostics;

                setBackendDiagnostics(publishDiagnostics);
                showToast(publish.compilePreview.canCompile ? 'Validation failed.' : 'Compile failed.', 'warning');
                return;
            }

            const publishedRevision = publish.compilePreview.definitionDocument.revision;

            const savedResult = {
                definitionId: publish.definitionId,
                version: publishedRevision,
                versionId: publish.versionId
            };

            setBackendDiagnostics([]);
            setHasUnsavedChanges(false);

            clearRuleEditorDraftSnapshot({
                definitionId,
                ideaLabVariantId,
                kind,
                saveContext,
                sourceDefinitionId: publishedSourceDefinitionId,
                sourceMode,
                sourceVersion: publishedSourceVersion
            });

            if (saveContext?.mode === 'strategy' && saveContext.strategyId !== null) {
                setLastSavedSummary(`Saved and applied as v${publishedRevision}`);
                showToast(`Saved ${kind} rules and applied v${publishedRevision} to Strategy ${saveContext.strategyId}.`, 'success');
                onSaved?.(savedResult, { appliedToStrategy });
                return;
            }

            setLastSavedSummary(`Saved as v${publishedRevision}`);

            showToast(
                saveContext?.mode === 'strategy'
                    ? `Saved ${kind} rules — v${publishedRevision}. Return to the strategy to attach them when it is created.`
                    : `Saved ${kind} rules — v${publishedRevision}.`,
                'success'
            );

            onSaved?.(savedResult, { appliedToStrategy: false });
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Save failed.';
            showToast(message, 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const isLoadingRegistry = manifestQuery.isLoading || featuresQuery.isLoading || templatesQuery.isLoading;

    if (isLoadingDraft) {
        return <div className="rounded-md border border-zinc-800 bg-zinc-950/60 p-6 text-sm text-zinc-400">Loading saved rules…</div>;
    }

    if (loadError) {
        return (
            <div className="rounded-md border border-red-900/70 bg-red-950/40 p-6 text-sm text-red-100">
                <div className="font-medium text-red-50">Unable to load rules</div>
                <div className="mt-2 text-red-100/90">{loadError}</div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {canLoadSwingExit && (
                <div className="space-y-2 rounded-md border border-zinc-800 p-4">
                    <Button
                        variant="secondary"
                        disabled={!swingExitQuery.data}
                        onClick={() => {
                            if (!swingExitQuery.data) return;
                            setDraft(hydrateTradeLogicDraftFromBackend(swingExitQuery.data));
                            setSelectedNodeId(null);
                            markDirty();
                        }}
                    >
                        Use staged swing exits
                    </Button>
                    <p className="text-sm text-zinc-400">
                        Stock backtests: frozen entry-session stop, resistance trim, breakeven protection and daily EMA exits. Pair with a
                        swing entry template that captures the stop and resistance.
                    </p>
                </div>
            )}
            <RuleEditorHeader
                description={draft.description ?? ''}
                errorCount={errorCount}
                hasUnsavedChanges={hasUnsavedChanges}
                isSaving={isSaving}
                kind={kind}
                lastSavedSummary={lastSavedSummary}
                name={draft.name}
                restoredDraftSavedAt={restoredDraftSavedAt}
                saveButtonLabel={saveButtonLabel}
                saveContextMessage={saveContextMessage}
                sourceDefinitionMessage={sourceDefinitionMessage}
                priceFillModel={draft.priceFillModel ?? null}
                onDescriptionChange={(description) => updateDraftMetadata({ description: description || null })}
                onNameChange={(name) => updateDraftMetadata({ name })}
                onPriceFillModelChange={(priceFillModel) => updateDraftMetadata({ priceFillModel })}
                onSave={() => {
                    handleSave().catch(() => undefined);
                }}
            />
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(320px,0.8fr)_minmax(0,1.2fr)]">
                <div className="space-y-4">
                    <AddRulePanel
                        actionTemplateOptions={computedState.actionTemplateOptions}
                        actionTemplateValues={builderInput.actionTemplateValues}
                        activeAddKind={builderInput.activeAddKind}
                        conditionTemplateOptions={computedState.conditionTemplateOptions}
                        conditionTemplateValueField={computedState.conditionTemplateValueField}
                        conditionTemplateValues={builderInput.conditionTemplateValues}
                        featureOptions={computedState.featureOptions}
                        featureSearch={builderInput.featureSearch}
                        filterOperators={builderInput.filterOperators}
                        filterValues={builderInput.filterValues}
                        filteredFeatureCount={computedState.filteredFeatureCount}
                        isLoadingRegistry={isLoadingRegistry}
                        localError={builderInput.localError}
                        selectedActionTemplate={computedState.selectedActionTemplate}
                        selectedActionTemplateKey={builderInput.selectedActionTemplateKey}
                        selectedConditionTemplate={computedState.selectedConditionTemplate}
                        selectedConditionTemplateFeatureOptionKey={builderInput.selectedConditionTemplateFeatureOptionKey}
                        selectedConditionTemplateKey={builderInput.selectedConditionTemplateKey}
                        selectedConditionTemplateOperator={builderInput.selectedConditionTemplateOperator}
                        selectedConditionTemplateValue={builderInput.selectedConditionTemplateValue}
                        selectedFeature={computedState.selectedFeature}
                        selectedFeatureConditionBlockingReason={computedState.selectedFeatureConditionBlockingReason}
                        selectedFeatureKey={builderInput.selectedFeatureKey}
                        selectedFeatureOperator={builderInput.selectedFeatureOperator}
                        selectedFeatureValue={builderInput.selectedFeatureValue}
                        selectedMissingDataPolicy={builderInput.selectedMissingDataPolicy}
                        selectorValues={builderInput.selectorValues}
                        showAdvancedFeatures={builderInput.showAdvancedFeatures}
                        totalFeatureCount={computedState.totalFeatureCount}
                        onActionTemplateValuesChange={(actionTemplateValues) => updateBuilder({ actionTemplateValues })}
                        onActiveAddKindChange={(activeAddKind) => updateBuilder({ activeAddKind })}
                        onAddActionTemplate={addActionTemplate}
                        onAddConditionTemplate={addConditionTemplate}
                        onAddFeatureCondition={addFeatureCondition}
                        onAddStructureNode={addStructureNode}
                        onConditionTemplateValuesChange={(conditionTemplateValues) => updateBuilder({ conditionTemplateValues })}
                        onFeatureSearchChange={(featureSearch) => updateBuilder({ featureSearch })}
                        onFilterOperatorsChange={(filterOperators) => updateBuilder({ filterOperators })}
                        onFilterValuesChange={(filterValues) => updateBuilder({ filterValues })}
                        onSelectActionTemplateKey={handleSelectActionTemplateKey}
                        onSelectConditionTemplateFeatureOptionKey={handleSelectConditionTemplateFeatureOptionKey}
                        onSelectConditionTemplateKey={handleSelectConditionTemplateKey}
                        onSelectFeatureKey={handleSelectFeatureKey}
                        onSelectedConditionTemplateOperatorChange={(selectedConditionTemplateOperator) =>
                            updateBuilder({ selectedConditionTemplateOperator })
                        }
                        onSelectedConditionTemplateValueChange={(selectedConditionTemplateValue) =>
                            updateBuilder({ selectedConditionTemplateValue, localError: null })
                        }
                        onSelectedFeatureOperatorChange={(selectedFeatureOperator) => updateBuilder({ selectedFeatureOperator })}
                        onSelectedFeatureValueChange={(selectedFeatureValue) => updateBuilder({ selectedFeatureValue, localError: null })}
                        onSelectedMissingDataPolicyChange={(selectedMissingDataPolicy) => updateBuilder({ selectedMissingDataPolicy })}
                        onSelectorValuesChange={(selectorValues) => updateBuilder({ selectorValues })}
                        onShowAdvancedFeaturesChange={(showAdvancedFeatures) => updateBuilder({ showAdvancedFeatures })}
                    />
                </div>
                <div className="space-y-4">
                    <DraftTreePanel
                        actionTemplatesByKey={templatesQuery.actionTemplatesByKey}
                        conditionTemplatesByKey={templatesQuery.conditionTemplatesByKey}
                        diagnostics={allDiagnostics}
                        draft={draft}
                        featuresByKey={featuresQuery.featuresByKey}
                        selectedNodeId={selectedNodeId}
                        onDuplicateNode={handleDuplicateNode}
                        onMoveNode={handleMoveNode}
                        onRemoveNode={handleRemoveNode}
                        onSelectNode={handleSelectNode}
                        onToggleNode={handleToggleNode}
                        onUnwrapNode={handleUnwrapNode}
                        onWrapNode={handleWrapNode}
                    />
                    <NodeInspectorPanel
                        actionTemplatesByKey={templatesQuery.actionTemplatesByKey}
                        conditionTemplatesByKey={templatesQuery.conditionTemplatesByKey}
                        definitionOptions={[{ value: '', label: 'Select definition' }]}
                        diagnostics={allDiagnostics}
                        featureRefValidationState={featureRefValidationState}
                        featuresByKey={featuresQuery.featuresByKey}
                        node={selectedNode}
                        priceWaitAvailable={draft.kind === 'entry' && draft.scope === 'asset'}
                        priceCaptureOptions={priceCaptureOptions}
                        onActionChange={handleActionChange}
                        onConditionChange={handleConditionChange}
                        onGroupChange={handleGroupChange}
                        onLabelChange={handleLabelChange}
                        onReferenceChange={handleReferenceChange}
                        onRemoveNode={handleRemoveNode}
                        onScheduleChange={handleScheduleChange}
                        onSequenceChange={handleSequenceChange}
                        onToggleNode={handleToggleNode}
                    />
                </div>
            </div>
        </div>
    );
};

interface RuleEditorHeaderProps {
    name: string;
    description: string;
    kind: 'entry' | 'exit';
    isSaving: boolean;
    hasUnsavedChanges: boolean;
    errorCount: number;
    lastSavedSummary: string | null;
    restoredDraftSavedAt: string | null;
    saveButtonLabel: string;
    saveContextMessage: string | null;
    sourceDefinitionMessage: string | null;
    priceFillModel: 'resting_level' | null;
    onNameChange: (value: string) => void;
    onDescriptionChange: (value: string) => void;
    onPriceFillModelChange: (value: 'resting_level' | null) => void;
    onSave: () => void;
}

const RuleEditorHeader = ({
    name,
    description,
    kind,
    isSaving,
    hasUnsavedChanges,
    errorCount,
    lastSavedSummary,
    restoredDraftSavedAt,
    saveButtonLabel,
    saveContextMessage,
    sourceDefinitionMessage,
    priceFillModel,
    onNameChange,
    onDescriptionChange,
    onPriceFillModelChange,
    onSave
}: RuleEditorHeaderProps) => (
    <div className="rounded-md border border-zinc-800 bg-zinc-950/60 p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                    <Badge variant="dark">{kind === 'entry' ? 'Entry rules' : 'Exit rules'}</Badge>
                    {hasUnsavedChanges && <Badge variant="warning">Unsaved changes</Badge>}
                    {!hasUnsavedChanges && lastSavedSummary && <Badge variant="success">{lastSavedSummary}</Badge>}
                    {restoredDraftSavedAt && <Badge variant="info">Recovered local draft</Badge>}
                    {errorCount > 0 && (
                        <Badge variant="danger">
                            {errorCount} error{errorCount === 1 ? '' : 's'}
                        </Badge>
                    )}
                </div>
                {restoredDraftSavedAt && (
                    <div className="text-xs text-amber-200/80">
                        Restored unsaved local edits from {new Date(restoredDraftSavedAt).toLocaleString()}.
                    </div>
                )}
                {saveContextMessage && <div className="text-xs text-zinc-400">{saveContextMessage}</div>}
                {sourceDefinitionMessage && <div className="text-xs text-zinc-500">{sourceDefinitionMessage}</div>}
                <FormInput
                    noMargin
                    id="rule-editor-name"
                    label="Name"
                    placeholder={kind === 'entry' ? 'e.g. SMA Crossover Long' : 'e.g. 5% stop, 10% target'}
                    value={name}
                    onChange={(event) => onNameChange(event.target.value)}
                />
                <FormInput
                    noMargin
                    id="rule-editor-description"
                    label="Description"
                    placeholder="What does this rule set do? (optional)"
                    value={description}
                    onChange={(event) => onDescriptionChange(event.target.value)}
                />
                {kind === 'exit' && (
                    <FormSelect
                        label="Historical exit fill"
                        helpText="Resting level fills at the touched stop or target during minute replay and is unavailable for live execution."
                        options={[
                            { value: 'observed_close', label: 'Observed candle close' },
                            { value: 'resting_level', label: 'Resting stop or target level' }
                        ]}
                        value={priceFillModel ?? 'observed_close'}
                        onChange={(event) => onPriceFillModelChange(event.target.value === 'resting_level' ? 'resting_level' : null)}
                    />
                )}
            </div>
            <div className="flex flex-col items-stretch gap-2 md:items-end">
                <Button disabled={isSaving || errorCount > 0} isLoading={isSaving} size="md" variant="primary" onClick={onSave}>
                    {isSaving ? 'Saving…' : saveButtonLabel}
                </Button>
            </div>
        </div>
    </div>
);

interface AddRulePanelProps {
    activeAddKind: DraftBuilderAddKind;
    isLoadingRegistry: boolean;
    featureOptions: { label: string; options: { value: string; label: string }[] }[];
    actionTemplateOptions: { value: string; label: string }[];
    conditionTemplateOptions: { value: string; label: string }[];
    conditionTemplateValueField: ReturnType<typeof buildConditionTemplateValueField>;
    selectedFeature: TradeLogicRegistryFeatureDto | null;
    selectedActionTemplate: TradeLogicRegistryActionTemplateDto | null;
    selectedConditionTemplate: TradeLogicRegistryConditionTemplateDto | null;
    selectedFeatureConditionBlockingReason: string | null;
    filteredFeatureCount: number;
    totalFeatureCount: number;
    featureSearch: string;
    selectedFeatureKey: string;
    selectedFeatureOperator: string;
    selectedFeatureValue: string;
    selectedMissingDataPolicy: '' | TradeLogicMissingDataPolicy;
    selectorValues: Record<string, string>;
    filterValues: Record<string, string>;
    filterOperators: Record<string, TradeLogicComparisonOperatorV2>;
    showAdvancedFeatures: boolean;
    selectedConditionTemplateKey: string;
    selectedConditionTemplateFeatureOptionKey: string;
    selectedConditionTemplateOperator: string;
    selectedConditionTemplateValue: string;
    conditionTemplateValues: Record<string, string>;
    selectedActionTemplateKey: string;
    actionTemplateValues: Record<string, string>;
    localError: string | null;
    onActiveAddKindChange: (kind: DraftBuilderAddKind) => void;
    onFeatureSearchChange: (value: string) => void;
    onSelectFeatureKey: (featureKey: string) => void;
    onSelectorValuesChange: (values: Record<string, string>) => void;
    onSelectedFeatureOperatorChange: (operator: string) => void;
    onSelectedFeatureValueChange: (value: string) => void;
    onSelectedMissingDataPolicyChange: (value: '' | TradeLogicMissingDataPolicy) => void;
    onFilterValuesChange: (values: Record<string, string>) => void;
    onFilterOperatorsChange: (operators: Record<string, TradeLogicComparisonOperatorV2>) => void;
    onShowAdvancedFeaturesChange: (value: boolean) => void;
    onAddFeatureCondition: () => void;
    onSelectConditionTemplateKey: (templateKey: string) => void;
    onSelectConditionTemplateFeatureOptionKey: (optionKey: string) => void;
    onSelectedConditionTemplateOperatorChange: (operator: string) => void;
    onSelectedConditionTemplateValueChange: (value: string) => void;
    onConditionTemplateValuesChange: (values: Record<string, string>) => void;
    onAddConditionTemplate: () => void;
    onSelectActionTemplateKey: (templateKey: string) => void;
    onActionTemplateValuesChange: (values: Record<string, string>) => void;
    onAddActionTemplate: () => void;
    onAddStructureNode: (kind: 'group' | 'sequence' | 'schedule') => void;
}

const AddRulePanel = (props: AddRulePanelProps) => {
    const { activeAddKind, isLoadingRegistry } = props;

    return (
        <div className="rounded-md border border-zinc-800 bg-zinc-950/40 p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
                <h5 className="text-base font-semibold text-zinc-100">Add a rule</h5>
                <div className="flex flex-wrap gap-1" role="tablist">
                    {ADD_RULE_TABS.map((option) => (
                        <Button
                            aria-selected={activeAddKind === option.value}
                            key={option.value}
                            role="tab"
                            size="sm"
                            variant={activeAddKind === option.value ? 'primary' : 'outline-dark'}
                            onClick={() => props.onActiveAddKindChange(option.value)}
                        >
                            {option.label}
                        </Button>
                    ))}
                </div>
            </div>
            {isLoadingRegistry ? (
                <div className="text-sm text-zinc-500">Loading rule library…</div>
            ) : activeAddKind === 'condition' ? (
                <>
                    <ConditionAddTab {...props} />
                    {props.conditionTemplateOptions.length > 1 && <ConditionTemplateAddTab {...props} />}
                </>
            ) : activeAddKind === 'action' ? (
                <ActionAddTab {...props} />
            ) : (
                <GroupAddTab onAddStructureNode={props.onAddStructureNode} />
            )}
        </div>
    );
};

const ConditionAddTab = (props: AddRulePanelProps) => {
    const { selectedFeature, selectedFeatureKey, featureSearch, selectedFeatureOperator, selectedFeatureValue } = props;

    return (
        <div className="space-y-3">
            <FeatureTypeahead
                label="Feature"
                options={props.featureOptions}
                placeholder="Type to search features…"
                searchValue={featureSearch}
                selectedLabel={selectedFeature?.displayLabel ?? null}
                selectedValue={selectedFeatureKey}
                onSearchChange={props.onFeatureSearchChange}
                onSelect={props.onSelectFeatureKey}
            />
            <div className="flex items-center justify-between text-xs text-zinc-500">
                <span>
                    {props.filteredFeatureCount} / {props.totalFeatureCount} features
                </span>
                <FormCheckbox
                    checked={props.showAdvancedFeatures}
                    id="rule-editor-show-advanced"
                    label="Show all features"
                    labelClassName="text-zinc-400"
                    onChange={(event) => props.onShowAdvancedFeaturesChange(event.target.checked)}
                />
            </div>
            {selectedFeature && (
                <div className="space-y-3 rounded-md border border-zinc-800 bg-black/20 p-3">
                    {selectedFeature.selectorFields.length > 0 && (
                        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                            {selectedFeature.selectorFields.map((field) => (
                                <SchemaFieldInput
                                    field={field}
                                    key={field.key}
                                    value={props.selectorValues[field.key] ?? ''}
                                    onChange={(value) => props.onSelectorValuesChange({ ...props.selectorValues, [field.key]: value })}
                                />
                            ))}
                        </div>
                    )}
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                        <FormSelect
                            label="Operator"
                            options={selectedFeature.allowedOperators.map((operator) => ({ value: operator, label: operator }))}
                            value={selectedFeatureOperator}
                            onChange={(event) => props.onSelectedFeatureOperatorChange(event.target.value)}
                        />
                        <FeatureConditionValueInput
                            feature={selectedFeature}
                            operator={selectedFeatureOperator}
                            value={selectedFeatureValue}
                            onChange={props.onSelectedFeatureValueChange}
                        />
                    </div>
                    <FormSelect
                        label="Missing data"
                        options={missingDataPolicyOptions}
                        value={props.selectedMissingDataPolicy}
                        onChange={(event) =>
                            props.onSelectedMissingDataPolicyChange(event.target.value as '' | TradeLogicMissingDataPolicy)
                        }
                    />
                    {selectedFeature.filterFields.length > 0 && (
                        <details className="rounded-md border border-zinc-800 bg-zinc-950/60 p-2">
                            <summary className="cursor-pointer text-xs font-semibold uppercase text-zinc-400">Feature filters</summary>
                            <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
                                {selectedFeature.filterFields.map((field) => (
                                    <div className="grid grid-cols-[minmax(0,1fr)_96px] gap-2" key={field.key}>
                                        <SchemaFieldInput
                                            field={field}
                                            value={props.filterValues[field.key] ?? ''}
                                            onChange={(value) => props.onFilterValuesChange({ ...props.filterValues, [field.key]: value })}
                                        />
                                        <FormSelect
                                            label="Op"
                                            options={(field.operators.length > 0 ? field.operators : ['=']).map((operator) => ({
                                                value: operator,
                                                label: operator
                                            }))}
                                            value={props.filterOperators[field.key] ?? field.operators[0] ?? '='}
                                            onChange={(event) =>
                                                props.onFilterOperatorsChange({
                                                    ...props.filterOperators,
                                                    [field.key]: event.target.value as TradeLogicComparisonOperatorV2
                                                })
                                            }
                                        />
                                    </div>
                                ))}
                            </div>
                        </details>
                    )}
                    <Button
                        disabled={props.selectedFeatureConditionBlockingReason !== null}
                        size="sm"
                        variant="primary"
                        onClick={props.onAddFeatureCondition}
                    >
                        Add condition
                    </Button>
                    {props.selectedFeatureConditionBlockingReason && (
                        <div className="rounded-md border border-amber-900/60 bg-amber-950/30 p-2 text-xs text-amber-100">
                            {props.selectedFeatureConditionBlockingReason}
                        </div>
                    )}
                </div>
            )}
            {props.localError && (
                <div className="rounded-md border border-red-900/60 bg-red-950/40 p-2 text-xs text-red-100">{props.localError}</div>
            )}
        </div>
    );
};

const ConditionTemplateAddTab = (props: AddRulePanelProps) => {
    const template = props.selectedConditionTemplate;

    return (
        <details className="mt-3 rounded-md border border-zinc-800 bg-zinc-950/60 p-3">
            <summary className="cursor-pointer text-xs font-semibold uppercase text-zinc-400">Use a condition template</summary>
            <div className="mt-3 space-y-2">
                <FormSelect
                    label="Template"
                    options={props.conditionTemplateOptions}
                    value={props.selectedConditionTemplateKey}
                    onChange={(event) => props.onSelectConditionTemplateKey(event.target.value)}
                />
                {template && (
                    <>
                        {template.contextFeatureOptions.length > 0 && (
                            <FormSelect
                                label="Feature option"
                                options={template.contextFeatureOptions.map((option) => ({
                                    value: option.optionKey,
                                    label: option.label
                                }))}
                                value={props.selectedConditionTemplateFeatureOptionKey}
                                onChange={(event) => props.onSelectConditionTemplateFeatureOptionKey(event.target.value)}
                            />
                        )}
                        <FormSelect
                            label="Operator"
                            options={(template.allowedOperators.length > 0 ? template.allowedOperators : ['=']).map((operator) => ({
                                value: operator,
                                label: operator
                            }))}
                            value={props.selectedConditionTemplateOperator || template.allowedOperators[0] || '='}
                            onChange={(event) => props.onSelectedConditionTemplateOperatorChange(event.target.value)}
                        />
                        {props.conditionTemplateValueField && (
                            <SchemaFieldInput
                                field={props.conditionTemplateValueField}
                                value={props.selectedConditionTemplateValue}
                                onChange={props.onSelectedConditionTemplateValueChange}
                            />
                        )}
                        {template.parameterFields.length > 0 && (
                            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                                {template.parameterFields.map((field) => (
                                    <SchemaFieldInput
                                        field={field}
                                        key={field.key}
                                        value={props.conditionTemplateValues[field.key] ?? ''}
                                        onChange={(value) =>
                                            props.onConditionTemplateValuesChange({ ...props.conditionTemplateValues, [field.key]: value })
                                        }
                                    />
                                ))}
                            </div>
                        )}
                        <Button size="sm" variant="primary" onClick={props.onAddConditionTemplate}>
                            Add condition
                        </Button>
                    </>
                )}
            </div>
        </details>
    );
};

const ActionAddTab = (props: AddRulePanelProps) => {
    const template = props.selectedActionTemplate;

    return (
        <div className="space-y-3">
            <FormSelect
                label="Action"
                options={props.actionTemplateOptions}
                value={props.selectedActionTemplateKey}
                onChange={(event) => props.onSelectActionTemplateKey(event.target.value)}
            />
            {template && (
                <div className="space-y-3 rounded-md border border-zinc-800 bg-black/20 p-3">
                    {template.parameterFields.length > 0 && (
                        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                            {template.parameterFields.map((field) => (
                                <SchemaFieldInput
                                    field={field}
                                    key={field.key}
                                    value={props.actionTemplateValues[field.key] ?? ''}
                                    onChange={(value) =>
                                        props.onActionTemplateValuesChange({ ...props.actionTemplateValues, [field.key]: value })
                                    }
                                />
                            ))}
                        </div>
                    )}
                    <Button size="sm" variant="primary" onClick={props.onAddActionTemplate}>
                        Add action
                    </Button>
                </div>
            )}
        </div>
    );
};

const GroupAddTab = ({ onAddStructureNode }: { onAddStructureNode: (kind: 'group' | 'sequence' | 'schedule') => void }) => (
    <div className="space-y-3">
        <p className="text-xs text-zinc-500">Add a container that wraps multiple rules together.</p>
        <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline-dark" onClick={() => onAddStructureNode('group')}>
                Add group (all / any)
            </Button>
            <Button size="sm" variant="outline-dark" onClick={() => onAddStructureNode('sequence')}>
                Add sequence (within X bars)
            </Button>
            <Button size="sm" variant="outline-dark" onClick={() => onAddStructureNode('schedule')}>
                Add schedule
            </Button>
        </div>
    </div>
);

export default TradeLogicRuleEditor;
